import React, { useState, useCallback, useContext, useEffect, useMemo } from 'react';
import { Input, Icon, Button, Card, Select, Form } from 'antd';
import { useHistory } from 'react-router-dom';
import { useGet, Context, useFetch } from 'store';

import FilterModal from './components/FilterModal';
import StatItem from './components/StatItem';
import logoBadge from '_src/assets/images/logo-badge.png';
import { PageContainer } from '_src/shared/styles';
import { Restricted } from '_src/shared/restrict';
import { AsyncTable, Loading } from '_src/components';
import { debounce, isEmptyObj, filterObjectEmptyValues } from '_src/utils';
import services from '_src/services';
import { Refresh } from '_src/components';
import {
  Header,
  Logo,
  Title,
  HeaderStatsWrapper,
  StatsContainer,
  SearchLabel,
  SearchWrapper,
  FilterWrapper
} from './styles';
import { PersistentRepo } from '_src/shared/repo';
const { Option } = Select;

const locationFiltersConfig = [
  { id: 0, label: 'Both' },
  { id: 1, label: 'Yard' },
  { id: 2, label: 'Classroom' }
];

function StudentRecords() {
  const { getData } = useContext(Context);
  const baseApi = GLOBALS.BASE_API;
  const path = 'reports/student_records/students_csv';
  const token = PersistentRepo.get('token');

  const getInitialLocation = () => {
    const location = PersistentRepo.get('location');

    const user = getData('user');
    const branchId = user.branchId || 1;

    let selectedFilterLocation = 0;
    if (location) {
      const type = location?.split('x')[0];
      if (type === 'classroom') {
        selectedFilterLocation = 2;
      }
      if (type === 'yard') {
        selectedFilterLocation = 1;
      }
    }

    const selectedBranch = location ? location : `allxallx${branchId}`;
    return { selectedBranch, selectedFilterLocation };
  };

  const { selectedBranch, selectedFilterLocation } = getInitialLocation();

  const resource = 'studentRecords';
  const config = services[resource];

  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  const [location, setLocation] = useState(selectedBranch);
  const [locationFilter, setLocationFilter] = useState(selectedFilterLocation);
  const [locationType, locationId, branchId] = (location || '')?.split('x');

  const [filterParams, setFilterParams] = useState({
    ...filterParams,
    selectedLocation: branchId,
    locationType: locationFilter
  });
  const [skip, setCurrentSkip] = useState(0);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const { data: recordsStats, busy: isLoading } = useGet(config.getStats);
  const { get: getLocations, busy } = useFetch({ ...config.getLocations });

  const history = useHistory();

  useEffect(() => {
    getLocations().then(data => {
      setAllLocations(data);
    });
  }, []);

  const debounceCallback = useCallback(
    debounce(
      (val, fieldName) => {
        setFilterParams(prevState => {
          return {
            ...prevState,
            [fieldName]: val
          };
        });
      },
      1500,
      false
    ),
    []
  );

  const locationFilterOptions = useMemo(() => {
    const branchLocations = (allLocations || []).filter(l => `${l.branchId}` === `${branchId}`);
    const branchTypes = branchLocations.map(l => l.type);
    return locationFiltersConfig.map(l => ({ ...l, disabled: !branchTypes.includes(l.id) }));
  }, [branchId, allLocations]);

  const getQueryParams = () => {
    let params;

    if (filterParams.contactNumber) {
      params = filterObjectEmptyValues({
        contact_number: filterParams.contactNumber,
        order_by: filterParams.orderBy,
        order: filterParams.order
      });
    } else {
      params = filterObjectEmptyValues({
        full_name: filterParams.fullName,
        contact_number: filterParams.contactNumber,
        permit: filterParams.hasPermit,
        branch_id: filterParams.selectedLocation,
        location_type:
          filterParams.studentTypes !== undefined
            ? filterParams.studentTypes
            : filterParams.locationType,
        status: filterParams.statuses,
        order_by: filterParams.orderBy,
        order: filterParams.order
      });
    }

    var queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    return queryString;
  };

  if (isLoading) {
    return <Loading />;
  }

  const handleDownload = async () => {
    setDownloadLoading(true);
    const url = `${baseApi}/${path}?limit=20&skip=${skip}&${getQueryParams()}`;

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'studentRecords.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      setDownloadLoading(false);
    } catch (error) {
      setDownloadLoading(false);
      console.error('Download error:', error);
    }
  };

  const onFilterBranch = (loc, resetLocationFilter) => {
    setLocation(loc);
    resetLocationFilter && setLocationFilter(0);

    PersistentRepo.set('location', loc);
    PersistentRepo.delete('allBranches');

    let selectedFilterLocation = 0;
    if (loc) {
      const type = loc?.split('x')[0];
      if (type === 'classroom') {
        selectedFilterLocation = 2;
      }
      if (type === 'yard') {
        selectedFilterLocation = 1;
      }
    }

    setFilterParams(prevState => {
      return {
        ...prevState,
        selectedLocation: loc.split('x')[2],
        locationType: selectedFilterLocation
      };
    });
  };

  const onFilterByLocation = val => {
    setLocationFilter(val);
    const selectedLocation = allLocations.find(
      l => `${l.branchId}` === `${branchId}` && l.type === val
    ).id;
    onFilterBranch(selectedLocation, false);
  };

  return (
    <PageContainer>
      <Refresh />
      <Card bodyStyle={{ paddingTop: 0 }}>
        <Header>
          <Logo src={logoBadge} />
          <HeaderStatsWrapper>
            <Title>Student Records</Title>
            <StatsContainer>
              <StatItem label="Total Students" value={recordsStats.studentsNumber} />
              <StatItem label="Graduated Students" value={recordsStats.studentsGraduatedNumber} />
              <StatItem label="Enrolled Students" value={recordsStats.studentsEnrolledNumber} />
              <StatItem label="Classroom" value={recordsStats.studentsClassroom} />
              <StatItem label="Yard" value={recordsStats.studentsYard} />
            </StatsContainer>
          </HeaderStatsWrapper>
        </Header>
        <SearchLabel>Find a Student:</SearchLabel>
        <FilterWrapper></FilterWrapper>
        <SearchWrapper>
          <Form.Item colon={false} label="Filter by Branch">
            <Select
              size="large"
              showSearch
              loading={busy}
              placeholder="Choose a Location"
              optionFilterProp="children"
              value={`allxallx${branchId}`}
              onChange={onFilterBranch}
              filterOption={(input, option) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              style={{
                minWidth: 210
              }}
              label="test"
            >
              {(allLocations || [])
                .filter(l => l.type === 0)
                .map(location => (
                  <Option key={location.id} value={location.id}>
                    {location.label}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item colon={false} label="Filter by Type">
            <Select
              size="large"
              onChange={onFilterByLocation}
              value={locationFilter}
              label="Filter by Type"
            >
              {(locationFilterOptions || []).map(l => (
                <Option key={l.id} value={l.id} disabled={l.disabled}>
                  {l.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Input
            size="large"
            placeholder="Search by first or last name"
            allowClear
            onChange={e => {
              debounceCallback(e.target.value, 'fullName');
            }}
            prefix={<Icon type="search" style={{ color: '#909090' }} />}
            style={{ width: 270 }}
            label="Search by first or last name"
          />

          <Input
            size="large"
            placeholder="Search by contact number"
            allowClear
            onChange={e => {
              debounceCallback(e.target.value, 'contactNumber');
            }}
            prefix={<Icon type="search" style={{ color: '#909090' }} />}
            style={{ width: 270 }}
          />

          <Button
            type="primary"
            style={{
              backgroundColor: '#E55F17',
              borderColor: '#E55F17',
              width: 200
            }}
            onClick={() => {
              setFilterModalVisible(true);
            }}
            icon="filter"
            size="large"
          >
            Filter Results
          </Button>

          <Restricted resource={'download_student_records'} method="GET">
            <Button
              loading={downloadLoading}
              type="primary"
              style={{
                backgroundColor: '#E55F17',
                borderColor: '#E55F17',
                width: 200
              }}
              onClick={handleDownload}
              icon="download"
              size="large"
            >
              Download CSV
            </Button>
          </Restricted>
        </SearchWrapper>
        <AsyncTable
          pagination
          onChange={(pagination, filters, sorter) => {
            // Dont reset page if no sorter has been applied
            if (isEmptyObj(sorter)) {
              return;
            }
            setFilterParams(prevState => {
              return {
                ...prevState,
                orderBy: sorter.column?.dbFieldName || '',
                order: sorter.order === 'ascend' ? 'asc' : 'desc'
              };
            });
          }}
          resetPageOn={[
            'fullName',
            'permit',
            'branchId',
            'locationType',
            'status',
            'orderBy',
            'order'
          ]}
          params={(() => {
            const baseParams = filterObjectEmptyValues({
              fullName: filterParams.fullName,
              contactNumber: filterParams.contactNumber,
              permit: filterParams.hasPermit,
              branchId: filterParams.selectedLocation,
              locationType:
                filterParams.studentTypes !== undefined
                  ? filterParams.studentTypes
                  : filterParams.locationType,
              status: filterParams.statuses,
              orderBy: filterParams.orderBy,
              order: filterParams.order
            });

            if (baseParams.contactNumber) {
              return {
                contactNumber: baseParams.contactNumber,
                orderBy: baseParams.orderBy,
                order: baseParams.order
              };
            }
            return baseParams;
          })()}
          fileName={resource}
          resource={resource}
          onRow={rowData => {
            return {
              onClick: () => {
                history.push(`/records/${rowData.id}`);
              }
            };
          }}
          onChangePage={setCurrentSkip}
        />
      </Card>

      <FilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        onFilter={params => {
          setFilterParams(prevState => {
            return {
              ...prevState,
              ...params
            };
          });
        }}
      />
    </PageContainer>
  );
}

export default StudentRecords;
